/**
 * useResourcePresence — real-time "who is working on / viewing which row" (shared).
 *
 * One broad WebSocket on `Basic?code=...|presence`; one upserted Basic per browser tab.
 * Rows are namespaced by `scope` + opaque resource id.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Basic, Bundle } from '@medplum/fhirtypes';
import { useFhirSubscription } from './useFhirSubscription';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';
import {
  registerPresenceController,
  unregisterPresenceController,
} from '@/lib/presence/presenceControllerRegistry';
import {
  buildPresenceBasic,
  parsePresence,
  presenceSearchQuery,
  presenceSessionIdentifierQuery,
  presenceSubscriptionCriteria,
  presenceTargetsEqual,
  recomputePresenceByRow,
  type PresenceActor,
  type PresenceSession,
  type PresenceTarget,
} from '@/lib/presence/presenceFhir';

export type { PresenceActor, PresenceTarget } from '@/lib/presence/presenceFhir';

const HEARTBEAT_MS = 5000;
const STALE_MS = 15000;
const PRUNE_MS = 3000;
const TAB_ID_STORAGE_KEY = 'unify-presence-tab-id';

export interface UseResourcePresenceReturn {
  /** Actors keyed by presenceRowKey(scope, rowId). */
  presenceByRow: Map<string, PresenceActor[]>;
  setActiveRow: (target: PresenceTarget | null) => void;
}

function getTabId(): string {
  try {
    let id = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(TAB_ID_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function deriveDisplayName(
  practitionerName: { given?: string[]; family?: string }[] | undefined,
): string {
  const n = practitionerName?.[0];
  if (!n) return 'Teammate';
  return [n.given?.join(' '), n.family].filter(Boolean).join(' ').trim() || 'Teammate';
}

export function useResourcePresence(): UseResourcePresenceReturn {
  const medplum = useMedplum();
  const { practitioner } = useCurrentUserPractitioner();

  const controllerIdRef = useRef(crypto.randomUUID());
  const tabIdRef = useRef<string>(getTabId());
  const sessionsRef = useRef<Map<string, PresenceSession>>(new Map());
  const activeTargetRef = useRef<PresenceTarget | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const myActorIdRef = useRef<string | undefined>(undefined);
  myActorIdRef.current = practitioner?.id;

  const [presenceByRow, setPresenceByRow] = useState<Map<string, PresenceActor[]>>(new Map());

  const recompute = useCallback(() => {
    setPresenceByRow(recomputePresenceByRow(sessionsRef.current.values(), myActorIdRef.current));
  }, []);

  const ingest = useCallback((basic: Basic) => {
    const session = parsePresence(basic);
    if (!session) return;
    if (session.active && session.expiresAtMs > Date.now()) {
      sessionsRef.current.set(session.sessionId, session);
    } else {
      sessionsRef.current.delete(session.sessionId);
    }
  }, []);

  const handleBundle = useCallback(
    (bundle: Bundle) => {
      let changed = false;
      for (const entry of bundle.entry ?? []) {
        if (entry.resource?.resourceType === 'Basic') {
          ingest(entry.resource as Basic);
          changed = true;
        }
      }
      if (changed) recompute();
    },
    [ingest, recompute],
  );

  const seed = useCallback(async () => {
    try {
      medplum.invalidateSearches('Basic');
      const results = await medplum.searchResources('Basic', presenceSearchQuery());
      sessionsRef.current.clear();
      for (const basic of results) ingest(basic);
      recompute();
    } catch {
      /* presence is best-effort */
    }
  }, [medplum, ingest, recompute]);

  const writePresence = useCallback(
    (target: PresenceTarget, active: boolean) => {
      const pract = practitioner;
      if (!pract?.id) return;
      const resource = buildPresenceBasic({
        practitioner: pract,
        tabId: tabIdRef.current,
        rowId: target.rowId,
        scope: target.scope,
        subjectResourceType: target.subjectResourceType,
        name: deriveDisplayName(pract.name),
        photoUrl: pract.photo?.[0]?.url,
        active,
        ttlMs: STALE_MS,
      });
      void medplum
        .upsertResource(resource, presenceSessionIdentifierQuery(pract.id, tabIdRef.current))
        .catch(() => {
          /* best-effort */
        });
    },
    [medplum, practitioner],
  );

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current != null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      const target = activeTargetRef.current;
      if (target && document.visibilityState === 'visible') {
        writePresence(target, true);
      }
    }, HEARTBEAT_MS);
  }, [stopHeartbeat, writePresence]);

  const setActiveRow = useCallback(
    (target: PresenceTarget | null) => {
      const prev = activeTargetRef.current;
      if (presenceTargetsEqual(target, prev)) return;
      activeTargetRef.current = target;
      if (target) {
        writePresence(target, true);
        startHeartbeat();
      } else {
        stopHeartbeat();
        if (prev) writePresence(prev, false);
      }
    },
    [writePresence, startHeartbeat, stopHeartbeat],
  );

  useFhirSubscription(
    presenceSubscriptionCriteria(),
    { onBundle: handleBundle, onReconnect: seed },
    '[ResourcePresence][WS]',
  );

  useEffect(() => {
    void seed();
  }, [seed]);

  useEffect(() => {
    registerPresenceController(controllerIdRef.current);
    return () => unregisterPresenceController(controllerIdRef.current);
  }, []);

  useEffect(() => {
    if (practitioner?.id && activeTargetRef.current) {
      writePresence(activeTargetRef.current, true);
      startHeartbeat();
    }
  }, [practitioner, writePresence, startHeartbeat]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [id, s] of sessionsRef.current) {
        if (s.expiresAtMs <= now) {
          sessionsRef.current.delete(id);
          changed = true;
        }
      }
      if (changed) recompute();
    }, PRUNE_MS);
    return () => clearInterval(timer);
  }, [recompute]);

  useEffect(() => {
    const handleVisibility = (): void => {
      const target = activeTargetRef.current;
      if (!target) return;
      if (document.visibilityState === 'hidden') {
        stopHeartbeat();
        writePresence(target, false);
      } else {
        writePresence(target, true);
        startHeartbeat();
      }
    };
    const handlePageHide = (): void => {
      const target = activeTargetRef.current;
      if (target) writePresence(target, false);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [writePresence, startHeartbeat, stopHeartbeat]);

  useEffect(() => {
    return () => {
      stopHeartbeat();
      const target = activeTargetRef.current;
      if (target) writePresence(target, false);
    };
  }, [stopHeartbeat, writePresence]);

  return useMemo(() => ({ presenceByRow, setActiveRow }), [presenceByRow, setActiveRow]);
}
